import { InputGroup } from '@/components/form/input-group'
import { Button } from '@/components/ui/button'
import { useAuthPageContext } from '@/features/auth/auth-context'
import { AUTH_STEP_QUERY_PARAMS, AUTH_STEPS, IAuthFormData } from '@/features/auth/helpers/auth-constants'
import { useWithLoading } from '@/utils/hooks/use-with-loading'
import { useAuth } from '@clerk/nextjs'
import { useSignIn, useSignUp } from '@clerk/nextjs/legacy'
import Image from 'next/image'
import { useFormContext } from 'react-hook-form'
import { UrlConfig } from '@/setup/routing/routing'
import { StepContainer } from '@/components/steps/step-container'
import { FixedBottomCtaBar } from '@/components/steps/fixed-bottom-cta-bar'
import { useCheckForSignInToken } from '../hooks/use-check-for-sign-token'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { makeHandleClerkError } from '@/utils/clerk/handle-clerk-error'
import { ClerkAPIError } from '@clerk/nextjs/types'
import { useCallback, useEffect, useState } from 'react'
import { toast } from '@/features/toast/toast'
import { useRouter, useSearchParams } from 'next/navigation'
import { getSafeRedirectUrl } from '@/utils/url/get-safe-redirect-url'
import { AuthInlineNavigation } from '@/features/auth/auth-inline-navigation'
import { Separator } from '@/components/ui/separator'
import { getMetadataMethods } from '@/features/auth/steps/auth-signup-step'
import { datadogLogs } from '@datadog/browser-logs'
import { setLocalStorageItem, getLocalStorageItem, LOCAL_STORAGE_KEYS } from '@/setup/local-storage'
import { AuthButtonWithLastUsedBadge } from '@/features/auth/components/auth-button-with-last-used-badge'
import { interceptInAppBrowser, isInAppBrowser } from '@/utils/browser/is-in-app-browser'
import { GoogleOauthBrowserCallout } from '@/features/auth/components/google-oauth-browser-callout'
import { cn } from '@/utils/styles/cn'
import { ATTRIBUTION_QUERY_PARAMS } from '@/features/attribution/utils/constants'

export const AuthSignInStep = () => {
    const router = useRouter()
    const { isLoaded: isAuthLoaded, isSignedIn } = useAuth()
    const { signIn } = useSignIn()
    const { signUp } = useSignUp()
    const { register, trigger, getValues, setError } = useFormContext<IAuthFormData>()
    const { setCurrentStep, boardReferralCode } = useAuthPageContext()
    const { withLoading: withSignInLoading, isLoading: isSignInLoading } = useWithLoading()
    const { withLoading: withGoogleSignInLoading, isLoading: isGoogleSignInLoading } = useWithLoading()
    const searchParams = useSearchParams()

    // Check for OAuth redirect_url parameter (used by external OAuth clients like ChatGPT)
    // Validate the URL to prevent open redirect attacks
    const oauthRedirectUrl = getSafeRedirectUrl(searchParams.get('redirect_url'))

    const routingUrl = UrlConfig['/routing'].getUrl()
    const { isLoading: isCheckForSignInTokenLoading } = useCheckForSignInToken(routingUrl)

    const [isWebView, setIsWebView] = useState(false)
    useEffect(() => {
        setIsWebView(isInAppBrowser())
    }, [])

    const lastUsedMethod = getLocalStorageItem<'google' | 'email'>(LOCAL_STORAGE_KEYS.Auth.LastUsedMethod)

    const makeHandleError = useCallback(
        (mode: 'toast' | 'inline-error') => (errors: ClerkAPIError[]) => {
            const firstError = errors[0]

            if (!firstError) return

            const message = firstError.message

            if (mode === 'toast') {
                toast.error(message)
            } else {
                setError('email', { message: message })
                datadogLogs.logger.error(message)
            }
        },
        [setError]
    )

    const handleSignIn = withSignInLoading(async () => {
        const isValid = await trigger(['email'])

        if (!isValid || !signIn) return
        if (!isAuthLoaded) return
        if (isSignedIn) {
            if (oauthRedirectUrl) {
                window.location.assign(oauthRedirectUrl)
                return
            }
            router.replace(routingUrl)
            return
        }

        setLocalStorageItem(LOCAL_STORAGE_KEYS.Auth.LastUsedMethod, 'email')

        const { email } = getValues()

        let supportedFirstFactors
        try {
            ;({ supportedFirstFactors } = await signIn.create({
                identifier: email,
            }))
        } catch (err) {
            makeHandleClerkError({ shouldRethrow: false, onAllErrors: makeHandleError('inline-error') })(err)
            return
        }

        // Find the email_code factor from supported first factors
        const emailCodeFactor = supportedFirstFactors?.find((factor) => factor.strategy === 'email_code')

        if (emailCodeFactor && 'emailAddressId' in emailCodeFactor) {
            // Prepare the first factor verification - sends OTP to email
            try {
                await signIn.prepareFirstFactor({
                    strategy: 'email_code',
                    emailAddressId: emailCodeFactor.emailAddressId,
                })
            } catch (err) {
                makeHandleClerkError({ shouldRethrow: false, onAllErrors: makeHandleError('toast') })(err)
                return
            }
        }

        setCurrentStep(AUTH_STEPS.CodeVerification, {
            [AUTH_STEP_QUERY_PARAMS.Email]: email,
            [AUTH_STEP_QUERY_PARAMS.AuthFlow]: 'sign-in',
            ...(oauthRedirectUrl ? { [ATTRIBUTION_QUERY_PARAMS.RedirectUrl]: oauthRedirectUrl } : {}),
        })
    })

    const handleGoogleSignIn = withGoogleSignInLoading(async () => {
        if (!signUp) return
        if (!isAuthLoaded) return
        if (isSignedIn) {
            if (oauthRedirectUrl) {
                window.location.assign(oauthRedirectUrl)
                return
            }
            router.replace(routingUrl)
            return
        }

        if (await interceptInAppBrowser()) return

        setLocalStorageItem(LOCAL_STORAGE_KEYS.Auth.LastUsedMethod, 'google')

        // Pass the OAuth redirect_url through to sso-callback if present
        const ssoCallbackUrl = oauthRedirectUrl
            ? `${window.location.origin}/auth/sso-callback?redirect_url=${encodeURIComponent(oauthRedirectUrl)}`
            : `${window.location.origin}/auth/sso-callback`

        // Use the OAuth redirect_url if present (for external OAuth flows like ChatGPT)
        // Otherwise redirect to the homepage
        const finalRedirectUrl = oauthRedirectUrl || window.location.origin + routingUrl

        const { getMetadata } = getMetadataMethods(boardReferralCode)

        // Use signUp instead of signIn so we can pass unsafeMetadata for the
        // transfer case (new user signing up through the sign-in page).
        // Clerk's transfer mechanism will auto-redirect to sign-in if the user already exists.
        await signUp
            .authenticateWithRedirect({
                strategy: 'oauth_google',
                redirectUrl: ssoCallbackUrl,
                redirectUrlComplete: finalRedirectUrl,
                ...getMetadata(),
            })
            .catch(makeHandleClerkError({ shouldRethrow: false, onAllErrors: makeHandleError('toast') }))
    })

    return (
        <>
            <div id="clerk-captcha" data-cl-theme="light" data-cl-size="flexible" data-cl-language="en-US" />

            <StepContainer className="max-md:pb-28">
                <div className="relative w-full">
                    {isCheckForSignInTokenLoading && (
                        <div className="absolute inset-0 z-20 flex flex-col items-start gap-3 md:items-center md:justify-center">
                            <LoadingSpinner size={20} />
                        </div>
                    )}

                    <div className={cn('w-full', isCheckForSignInTokenLoading && 'pointer-events-none')}>
                        {!isWebView && (
                            <AuthButtonWithLastUsedBadge showBadge={lastUsedMethod === 'google'}>
                                <Button
                                    size="ds2-responsive"
                                    variant={'outline'}
                                    className="w-full rounded-[10px] border-black/10 bg-white/60 transition-colors duration-200 ease-out hover:bg-black/4 disabled:bg-white/60"
                                    isLoading={isGoogleSignInLoading}
                                    onClick={handleGoogleSignIn}
                                >
                                    <span className="inline-flex items-center justify-center gap-2 w-full">
                                        <Image src="/icons/google.svg" alt="Google" width={20} height={20} />
                                        Continue with Google
                                    </span>
                                </Button>
                            </AuthButtonWithLastUsedBadge>
                        )}

                        {isWebView && <GoogleOauthBrowserCallout flow="sign-in" className="mb-1" />}

                        <Separator className="my-3" orientation="horizontal">
                            or
                        </Separator>

                        <InputGroup
                            required
                            placeholder="Your Email"
                            type="email"
                            label="Email"
                            containerClassName="mb-3 w-full"
                            onSubmit={handleSignIn}
                            autoComplete="email"
                            render={({ field, renderDefaultInput }) => (
                                <AuthButtonWithLastUsedBadge showBadge={lastUsedMethod === 'email'}>
                                    {renderDefaultInput(field)}
                                </AuthButtonWithLastUsedBadge>
                            )}
                            {...register('email')}
                        />

                        <FixedBottomCtaBar topMarginClassName="mt-0">
                            <Button
                                size="ds2-responsive"
                                variant={'ds2-primary'}
                                onClick={handleSignIn}
                                isLoading={isSignInLoading}
                                className="w-full"
                            >
                                Log in
                            </Button>
                        </FixedBottomCtaBar>

                        <AuthInlineNavigation
                            className="mt-3"
                            text="Don't have an account yet?"
                            ctaText="Sign up"
                            onClick={() => setCurrentStep(AUTH_STEPS.SignUp)}
                        />
                    </div>
                </div>
            </StepContainer>
        </>
    )
}
